import React, { createContext, useContext, useMemo } from 'react'
import { t } from 'i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { CustomCheckBox } from 'Components/CustomCheckBox'

const PickingFlowSelectionContext = createContext(null)

export const PickingFlowSelectionProvider = ({
  children,
  selectedBranchIds,
  onToggleSelect,
  onToggleSelectAll,
  allBranchIds,
}) => {
  const value = useMemo(
    () => ({
      selectedBranchIds,
      onToggleSelect,
      onToggleSelectAll,
      allBranchIds,
    }),
    [selectedBranchIds, onToggleSelect, onToggleSelectAll, allBranchIds]
  )

  return (
    <PickingFlowSelectionContext.Provider value={value}>
      {children}
    </PickingFlowSelectionContext.Provider>
  )
}

const SelectAllBranchesCheckbox = () => {
  const { selectedBranchIds, onToggleSelectAll, allBranchIds } = useContext(
    PickingFlowSelectionContext
  )
  const selectedVisibleCount = allBranchIds.filter(branchId =>
    selectedBranchIds.has(branchId)
  ).length
  const allSelected =
    allBranchIds.length > 0 && selectedVisibleCount === allBranchIds.length
  const someSelected =
    selectedVisibleCount > 0 && selectedVisibleCount < allBranchIds.length

  return (
    <div className="picking-flow-checkbox-wrapper">
      <CustomCheckBox
        checked={allSelected}
        indeterminate={someSelected}
        onChange={onToggleSelectAll}
        aria-label={t('PICKING_FLOW_SETTINGS.LIST.SELECT_ALL_BRANCHES')}
        wrapperTestId="container-checkbox-picking-flow-select-all"
        data-testid="checkbox-picking-flow-select-all"
      />
    </div>
  )
}

const BranchCheckbox = ({ row }) => {
  const { selectedBranchIds, onToggleSelect } = useContext(
    PickingFlowSelectionContext
  )
  const { BranchOfficeId, Name } = row.original

  return (
    <div
      className="picking-flow-checkbox-wrapper"
      data-testid={`container-checkbox-picking-flow-branch-${BranchOfficeId}`}>
      <CustomCheckBox
        checked={selectedBranchIds.has(BranchOfficeId)}
        onChange={() => onToggleSelect(BranchOfficeId)}
        aria-label={t('PICKING_FLOW_SETTINGS.LIST.SELECT_BRANCH', {
          name: Name,
        })}
        data-testid={`checkbox-picking-flow-branch-${BranchOfficeId}`}
      />
    </div>
  )
}

function useColumnPickingFlow({ onDelete }) {
  return useMemo(
    () => [
      {
        Header: SelectAllBranchesCheckbox,
        accessor: 'select',
        width: 25,
        minWidth: 25,
        maxWidth: 25,
        Cell: BranchCheckbox,
      },
      {
        Header: t('PICKING_FLOW_SETTINGS.LIST.NAME'),
        accessor: 'Name',
        Cell: ({ value }) => value || '—',
      },
      {
        Header: t('PICKING_FLOW_SETTINGS.LIST.COMPANY'),
        accessor: 'CompanyName',
        Cell: ({ value }) => value || '—',
      },
      {
        Header: t('PICKING_FLOW_SETTINGS.LIST.ACTIONS'),
        accessor: 'actions',
        Cell: ({ row }) => (
          <div
            className="picking-flow-actions"
            data-testid={`actions-picking-flow-branch-${row.original.BranchOfficeId}`}>
            <button
              type="button"
              onClick={() => onDelete(row.original)}
              aria-label={t('PICKING_FLOW_SETTINGS.LIST.DELETE_BRANCH', {
                name: row.original.Name,
              })}
              data-testid={`btn-delete-picking-flow-branch-${row.original.BranchOfficeId}`}>
              <FontAwesomeIcon
                icon={faTrashAlt}
                style={{ color: 'var(--error-color)', fontSize: '2rem' }}
              />
            </button>
          </div>
        ),
      },
    ],
    [onDelete]
  )
}

export default useColumnPickingFlow
